import React from 'react';
import { Col, Form, FormInstance, Row } from 'antd';

import { FilteredRecruitmentsTable } from '../../common/FilteredRecruitmentsTable';
import { Filter } from '../../common/filters.type';
import { SearchFormFieldEnum } from '../../common/searchFormField.types';
import { CriteriaOperatorEnum, StatusType } from '../../common/types';
import { useGetProject } from '../../common/useGetProject';
import { useGetRecruitmentStatusDictionary } from '../../common/useGetRecruitmentStatusDictionary';
import { FormDoubleInput } from '../../components/forms/FormDoubleInput';
import { FormProjectSelectWithSearch } from '../../components/forms/FormProjectSelectWithSearch';
import { FormSelect } from '../../components/forms/FormSelect';
import { FormTextArea } from '../../components/forms/FormTextArea';
import { responsibleOptions } from '../recruitment-details/StatusAndNoteForm';
import { useCandidateRecruitmentsTableColumnDefinitions } from '../recruitments/useRecruitmentsTableColumnDefinitions';

type AddCandidateToRecruitmentFormProps = {
  form: FormInstance;
};

const mapErrorFieldsToFormLabels = {
  projectUuid: 'Project',
  project: 'Project',
  candidate: 'Candidate',
  status: 'Status',
  responsible: 'Responsible',
};

const AddCandidateToRecruitmentForm: React.FC<AddCandidateToRecruitmentFormProps> = ({ form }) => {
  const { data: statusesData, isLoading: statusesLoading } = useGetRecruitmentStatusDictionary();
  const statusesOptions =
    !statusesLoading && statusesData
      ? statusesData.map((status) => ({
          value: status.id,
          content: status.name,
        }))
      : [];

  const currentStatusName = Form.useWatch('status', form);
  const currentStatus = statusesData?.find((status) => status.id === currentStatusName);

  const currentProjectId = Form.useWatch('projectUuid', form);
  const { data: projectData } = useGetProject(currentProjectId);

  return (
    <Form form={form} style={{ flexGrow: '1' }} layout="vertical" colon={false}>
      <Row gutter={[10, 5]}>
        <Col span={12}>
          <FormProjectSelectWithSearch name="projectUuid" required activeProjects />

          <FormDoubleInput
            leftInput={
              <FormSelect name="status" label="Status" allowClear={false} options={statusesOptions} required />
            }
            rightInput={
              currentStatus?.actionRequired && (
                <FormSelect
                  name="responsible"
                  label="Responsible"
                  allowClear={false}
                  options={responsibleOptions(currentStatus, projectData)}
                  required
                />
              )
            }
          />
        </Col>
        <Col span={12}>
          <FormTextArea label="Note content" name="content" rows={4} />
        </Col>
      </Row>
    </Form>
  );
};

export const CandidateRecruitmentsTable: React.FC<{ candidateId: string }> = ({ candidateId }) => {
  const defaultFilter: Filter = {
    id: 0,
    name: '',
    isDefault: false,
    sort: [],
    criteria: [
      {
        field: SearchFormFieldEnum.CandidateUUID,
        value: candidateId,
        operator: CriteriaOperatorEnum.EQ,
      },
    ],
  };

  const mapRecordToPayload = (record: { projectUuid: string; responsible: string; status: StatusType }) => ({
    data: {
      candidateUuid: candidateId,
      projectUuid: record['projectUuid'],
      responsible: record.responsible,
      status: record.status,
    },
  });

  return (
    <FilteredRecruitmentsTable
      defaultFilter={defaultFilter}
      itemForm={AddCandidateToRecruitmentForm}
      getColumnsDefinition={useCandidateRecruitmentsTableColumnDefinitions}
      mapRecordToSavePayload={mapRecordToPayload}
      mapErrorFieldsToFormLabels={mapErrorFieldsToFormLabels}
    />
  );
};
